import { AxiosError } from 'axios';
import { HTTP_ERROR_STATUS_CODES } from 'src/common/constants';
import {
  CurrentUserModuleResponseDto,
  CurrentUserResponseDto,
  ModuleDtoCameraPositionEnum,
} from 'src/generated/rest-api/types';
import {
  LOCAL_STORAGE_KEYS,
  LocalStorageInstance,
} from 'src/lib/local-storage';
import { RestAPIInstance } from 'src/lib/rest-api';
import { create } from 'zustand';
import { useAdminStore } from './useAdminStore';

interface AuthState {
  authToken: string | null;
  isAuthorized: boolean | null;
}

interface AuthStateChangePayload {
  authToken: string | null;
}

interface AuthStore {
  changeAuthState: (newState: AuthStateChangePayload) => void;
  onMounted: () => VoidFunction;
  resetAuthState: () => void;
  state: AuthState;
}

export const useAuthStore = create<AuthStore>()((set) => {
  const authToken = LocalStorageInstance.getItem(
    LOCAL_STORAGE_KEYS.AUTH_TOKEN_JWT,
  );
  const resetAuthState = () => {
    LocalStorageInstance.removeItem(LOCAL_STORAGE_KEYS.AUTH_TOKEN_JWT);
    // The admin token lives in its own store/localStorage key and is
    // otherwise only cleared by a couple of call sites remembering to do
    // it individually (easy to miss - see the mobile logout menu, which
    // didn't). Tying it to the one function that already runs on every
    // logout and every automatic 401 means a stale admin credential can
    // never outlive the session that granted it.
    useAdminStore.getState().removeAdminAuthToken();
    // currentUser (and its role) must not survive into the next session -
    // app-router.tsx's admin-token latch effect reads currentUser.role
    // synchronously on the next authToken change, before initStore's refetch
    // resolves, so a stale ADMIN role here would wrongly grant the next
    // logged-in user (e.g. a customer) an adminAuthToken.
    useCurrentUserStore.setState({
      currentUser: {} as CurrentUserResponseDto,
      farmsortModule: undefined,
      farmsortModules: [],
    });
    set({
      state: {
        authToken: null,
        isAuthorized: false,
      },
    });
  };
  return {
    changeAuthState: (newState) => {
      newState.authToken
        ? LocalStorageInstance.setItem(
            LOCAL_STORAGE_KEYS.AUTH_TOKEN_JWT,
            newState.authToken,
          )
        : LocalStorageInstance.removeItem(LOCAL_STORAGE_KEYS.AUTH_TOKEN_JWT);
      set({
        state: {
          authToken: newState.authToken,
          isAuthorized: !!newState.authToken,
        },
      });
    },
    onMounted: () => {
      const authRequestInterceptor =
        RestAPIInstance.httpClient.interceptors.request.use((request) => {
          const authToken = LocalStorageInstance.getItem(
            LOCAL_STORAGE_KEYS.AUTH_TOKEN_JWT,
          );
          // get the admin auth token
          const adminAuthToken = LocalStorageInstance.getItem(
            LOCAL_STORAGE_KEYS.ADMIN_AUTH_TOKEN_JWT,
          );

          if (authToken) {
            request.headers['Authorization'] = `Bearer ${authToken}`;
          }
          if (adminAuthToken) {
            request.headers['X-Admin-Authorization'] =
              `Bearer ${adminAuthToken}`;
          }

          return request;
        });

      const authResponseInterceptor =
        RestAPIInstance.httpClient.interceptors.response.use(
          (response) => response,
          (error) => {
            if (
              (error as AxiosError).response?.status ===
              HTTP_ERROR_STATUS_CODES.UNAUTHORIZED
            ) {
              resetAuthState();
            }

            return Promise.reject(error);
          },
        );

      return () => {
        RestAPIInstance.httpClient.interceptors.request.eject(
          authRequestInterceptor,
        );
        RestAPIInstance.httpClient.interceptors.response.eject(
          authResponseInterceptor,
        );
      };
    },
    resetAuthState,
    state: {
      authToken,
      isAuthorized: !!authToken,
    },
  };
});

interface GlobalSettingsState {
  notificationSidebarVisible: boolean;
  setNotificationSidebarVisible: (visible: boolean) => void;
}

export const useGlobalSettingsStore = create<GlobalSettingsState>()((set) => ({
  notificationSidebarVisible: true,
  setNotificationSidebarVisible: (visible) => {
    set({ notificationSidebarVisible: visible });
  },
}));

interface CurrentUserStore {
  currentUser: CurrentUserResponseDto;
  farmsortModule?: CurrentUserModuleResponseDto;
  farmsortModules: CurrentUserModuleResponseDto[];
  initStore: VoidFunction;
}

export const useCurrentUserStore = create<CurrentUserStore>()((set) => {
  const initStore = () => {
    void RestAPIInstance.apis.users
      .usersControllerFindCurrentUser()
      .then((response) => {
        set({
          currentUser: response.data,
          farmsortModule: response.data.modules.find(
            (module) =>
              module.type === ModuleDtoCameraPositionEnum.Farmsort &&
              module.shouldDisplayLiveData,
          ),
          farmsortModules: response.data.modules.filter(
            (module) => module.type === ModuleDtoCameraPositionEnum.Farmsort,
          ),
        });
      });
  };

  return {
    currentUser: {} as CurrentUserResponseDto,
    farmsortModules: [],
    initStore,
  };
});
